import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
  }

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => this.#updateStickyState());
  };

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // A box marked data-rbl-gift-hold (the Plants page offer) adds a free gift line right after the shopper's own
    // add. Its script hangs a promise on the event (`rblGiftSettled`); the drawer opens once that settles, so the
    // shopper never sees the cart without the gift. Anywhere else this changes nothing.
    const heldForGift = event.action === 'add' && event.target instanceof Element && event.target.closest('[data-rbl-gift-hold]') !== null;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !heldForGift && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    // The same box opens the drawer in the tap itself, with "Adding to your bag..." where the lines will be, instead
    // of after the add has come back (that wait is Shopify's, and is the longest on a slow phone). The lines take the
    // message's place as soon as the cart holds them; if the add fails the drawer closes again.
    const openedEarly = shouldAutoOpen && heldForGift && !sourceModal && !this.#themeDrawer?.isOpen;
    const drawer = this.#themeDrawer;
    let pendingTimer = 0;
    /** @type {MutationObserver | null} */
    let linesWatcher = null;
    const endPending = async (/** @type {boolean} */ close) => {
      clearTimeout(pendingTimer);
      linesWatcher?.disconnect();
      if (!openedEarly || !drawer?.hasAttribute('data-rbl-pending')) return;
      if (close) {
        drawer.removeAttribute('data-rbl-pending');
        if (this.#isCartEmpty()) drawer.close();
        return;
      }
      // Let the drawer paint the new lines before the message comes off. A hidden tab pauses frames, so each wait is
      // capped.
      const nextFrame = () => new Promise((resolve) => { requestAnimationFrame(resolve); setTimeout(resolve, 100); });
      for (let frame = 0; frame < 30 && this.#isCartEmpty(); frame++) await nextFrame();
      await nextFrame();
      drawer.removeAttribute('data-rbl-pending');
      this.#updateStickyState();
    };
    if (openedEarly && drawer) {
      drawer.setAttribute('data-rbl-pending', '');
      drawer.open();
      pendingTimer = window.setTimeout(() => endPending(false), 10000);
      // The lines can arrive (from the add response) before the cart update settles: take the message off as soon as
      // the drawer holds them.
      linesWatcher = new MutationObserver(() => {
        if (!this.#isCartEmpty()) endPending(false);
      });
      linesWatcher.observe(drawer, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
    }

    event.promise
      ?.then(async ({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          endPending(true);
          settle();
          return;
        }

        if (heldForGift) {
          const giftSettled = /** @type {{ rblGiftSettled?: Promise<void> }} */ (/** @type {unknown} */ (event)).rblGiftSettled;
          // Never hold the drawer for more than a few seconds, whatever happens to the gift.
          if (giftSettled) await Promise.race([giftSettled, new Promise((resolve) => setTimeout(resolve, 5000))]);
          await endPending(false);
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        endPending(true);
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
